import React, {useState, useEffect} from "react";

// Redux
import {useSelector, useDispatch} from "react-redux";
import {
  fetchPackageRegular,
  editPackageRegular,
  deletePackageRegular,
} from "../../../redux/features/packages/regularSlice";

// Routing
import {useParams, useHistory, Redirect} from "react-router-dom";

// Components
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "../../UI/MainPaper";
import PackageRegularForm from "./PackageRegularForm";
import FullContainerProgress from "../../UI/FullContainerProgress";
import DeleteButton from "../../Buttons/DeleteButton";
import DeleteDialog from "../../UI/DeleteDialog";
import BreadcrumbsWithEdit from "../../Navigation/BreadcrumbsWithEdit";

const PackageRegularEdit = () => {
  // Local State
  const [isEditMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Routing
  const {id} = useParams();
  const history = useHistory();

  // Redux
  const dispatch = useDispatch();
  const {currentPagePackageRegular, isLoading, error} = useSelector(
    state => state.packages
  );
  const {userPermission} = useSelector(state => {
    return state.auth;
  });
  const {write, delete: deletePermission} = userPermission.packages;

  const formattedValues = {
    id: currentPagePackageRegular.id,
    name: currentPagePackageRegular.name,
    permalink: currentPagePackageRegular.permalink,
    tagline: currentPagePackageRegular.tagline,
    description: currentPagePackageRegular.description,
    short_description: currentPagePackageRegular.short_description,
    gallery_id: currentPagePackageRegular.gallery_id,
    gallery: {id: currentPagePackageRegular.gallery_id},
    featured: currentPagePackageRegular.featured,
    is_fixed: currentPagePackageRegular.is_fixed,
    custom_rate: currentPagePackageRegular.custom_rate,
    hosted_in: currentPagePackageRegular.hosted_in,
    min_stay: currentPagePackageRegular.min_stay,
    languages: currentPagePackageRegular.languages,
    disclaimer: currentPagePackageRegular.disclaimer,
    package_status: currentPagePackageRegular.package_status,
    order_index: currentPagePackageRegular.order_index,
    hotels: currentPagePackageRegular.hotels,
    addonsProducts: {
      addons: currentPagePackageRegular.addons,
      products: currentPagePackageRegular.products,
    },
    categories: currentPagePackageRegular.categories,
    dates: currentPagePackageRegular.dates,

    // This is used to display the selected picture in the edit form
    photoObj: {
      photoId: currentPagePackageRegular.photoId,
      photoName: currentPagePackageRegular.photoName || "",
    },
  };

  let breadcrumbNameMap = {
    "/packages": "Packages",
    "/packages/regular": "Regular Packages",
  };
  breadcrumbNameMap[`/packages/regular/${id}`] = currentPagePackageRegular.name;

  useEffect(() => {
    // check if the id is a int to keep going
    // This if is why javascript is broken :)
    if (Number.isInteger(id * 1) && id * 1 < Number.MAX_SAFE_INTEGER) {
      dispatch(fetchPackageRegular(id));
    } else {
      history.push("/404");
    }
  }, [dispatch, history, id]);

  useEffect(() => {
    setEditMode(write);
  }, [write]);

  const onSubmit = async values => {
    const packageObj = {
      id,
      ...values,
    };
    // Logic to show error or success
    const res = await dispatch(editPackageRegular(packageObj));
    if (!res.error) {
      history.push({
        pathname: "/packages/regular",
        state: {
          showSuccess: true,
          snackbarContent: `Your changes to ${packageObj.name} were saved successfully!`,
          snackbarLink: `/packages/regular/${packageObj.id}`,
        },
      });
    }
  };

  const onDelete = async () => {
    // Logic to show error or success
    const res = await dispatch(deletePackageRegular(id));
    if (!res.error) {
      history.push({
        pathname: "/packages/regular",
        state: {
          showSuccess: true,
          snackbarContent: `The Regular Package was successfully deleted!`,
        },
      });
    }
  };

  // Redirect if addon doesn't exist'
  if (error && error.errorCode === 404) {
    return <Redirect to={"/404"} />;
  }

  return (
    <>
      <Grid container spacing={2}>
        <BreadcrumbsWithEdit
          isLoading={isLoading}
          breadcrumbNameMap={breadcrumbNameMap}
          isEditMode={isEditMode}
          setEditMode={setEditMode}
          writePermission={write}
        />
        <Grid item xs={12}>
          <Paper>
            {isLoading ? (
              <FullContainerProgress />
            ) : (
              <>
                <Typography component="h1" variant="h5" gutterBottom>
                  {currentPagePackageRegular.name}
                </Typography>
                <PackageRegularForm
                  defaultValues={formattedValues}
                  readOnly={!isEditMode}
                  submitButtonLabel="Save Changes"
                  onSubmit={onSubmit}
                  onCancel={() => setEditMode(false)}
                />
              </>
            )}
          </Paper>
        </Grid>
        {isEditMode && !isLoading && !!deletePermission && (
          <Grid item xs={12}>
            <DeleteButton onClick={() => setDialogOpen(true)} size="large">
              Delete Regular Package
            </DeleteButton>
          </Grid>
        )}
      </Grid>

      {!!deletePermission && (
        <DeleteDialog
          dialogTitle="Are you sure you want to delete this Package?"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={() => onDelete()}
        />
      )}
    </>
  );
};

export default PackageRegularEdit;
